// src/hooks/useJobUsers.js
import {useCallback, useState} from "react";
import toast from "react-hot-toast";
import {getInitials} from "../utils/stringUtils";

const USER_COLORS = [
  "#3498db", "#e74c3c", "#2ecc71", "#f39c12",
  "#9b59b6", "#1abc9c", "#e67e22", "#34495e",
];

/**
 * Hook to fetch and cache users with permissions for a job
 * @param {string} apiUrl - Base API URL
 * @returns {Object} { jobUsers, loadingUsersByJob, fetchJobPermissionUsers }
 */
export const useJobUsers = (apiUrl) => {
  const [jobUsers, setJobUsers] = useState({});
  const [loadingUsersByJob, setLoadingUsersByJob] = useState({});

  const fetchJobPermissionUsers = useCallback(
    async (jobId) => {
      if (jobUsers[jobId] || loadingUsersByJob[jobId]) return;
      setLoadingUsersByJob((prev) => ({ ...prev, [jobId]: true }));
      try {
        const response = await fetch(`${apiUrl}/api/Permissions/permissions-by-job/${jobId}`);
        if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
        const data = await response.json();
        const users = data.permissions || [];
        const formattedUsers = users.map((user, index) => ({
          id: user.userId,
          name: user.fullName || user.userName,
          username: user.userName,
          initials: getInitials(user.fullName || user.userName),
          color: USER_COLORS[index % USER_COLORS.length],
          ...user,
        }));
        setJobUsers((prev) => ({ ...prev, [jobId]: formattedUsers }));
      } catch (error) {
        console.error(`Error fetching users for job ${jobId}:`, error);
        toast.error(`Failed to load users for job`);
      } finally {
        setLoadingUsersByJob((prev) => ({ ...prev, [jobId]: false }));
      }
    },
    [apiUrl, jobUsers, loadingUsersByJob]
  );

  return { jobUsers, loadingUsersByJob, fetchJobPermissionUsers };
};